// Build: 2026-01-31 04:36:18 UTC
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Home, DollarSign, Calendar, 
  BarChart3, Copy, Check, Mail, Share2, Download,
  MapPin, RefreshCw, ChevronRight, Building2, Users,
  Percent, Clock, Thermometer, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple line chart component
const SimpleLineChart = ({ data, valueKey = 'value', height = 200 }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[valueKey]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((d[valueKey] - min) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div style={{ height }} className="relative">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {/* Grid lines */}
        <line x1="0" y1="25" x2="100" y2="25" stroke="currentColor" strokeOpacity="0.1" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="currentColor" strokeOpacity="0.1" />
        <line x1="0" y1="75" x2="100" y2="75" stroke="currentColor" strokeOpacity="0.1" />
        
        {/* Gradient fill */}
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {/* Area fill */}
        <polygon 
          points={`0,100 ${points} 100,100`} 
          fill="url(#chartGradient)"
          className="text-primary"
        />
        
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary"
        />
      </svg>
      
      {/* Labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-muted-foreground px-1">
        <span>{data[0]?.date?.slice(0, 7)}</span>
        <span>{data[data.length - 1]?.date?.slice(0, 7)}</span>
      </div>
    </div>
  );
};

// Copy button component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
};

const MarketData = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('FL');
  const [selectedCity, setSelectedCity] = useState('Miami');
  const [marketSnapshot, setMarketSnapshot] = useState(null);
  const [mortgageData, setMortgageData] = useState(null);
  const [hpiData, setHpiData] = useState(null);
  const [redfinData, setRedfinData] = useState(null);
  const [redfinMetro, setRedfinMetro] = useState(null);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Use ref to track current request and prevent race conditions
  const currentRequestRef = useRef(0);
  
  const authHeaders = { Authorization: `Bearer ${token}` };
  
  // State and city data structure
  const statesWithCities = {
    'FL': { name: 'Florida', cities: ['Miami', 'Tampa', 'Orlando', 'Jacksonville', 'Fort Lauderdale', 'West Palm Beach', 'Naples', 'Sebring', 'Fort Myers', 'Lehigh Acres'] },
    'CA': { name: 'California', cities: ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento'] },
    'TX': { name: 'Texas', cities: ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth'] },
    'NY': { name: 'New York', cities: ['New York', 'Buffalo', 'Rochester', 'Albany'] },
    'AZ': { name: 'Arizona', cities: ['Phoenix', 'Tucson', 'Mesa', 'Scottsdale'] },
    'NV': { name: 'Nevada', cities: ['Las Vegas', 'Reno', 'Henderson'] },
    'CO': { name: 'Colorado', cities: ['Denver', 'Colorado Springs', 'Aurora', 'Boulder'] },
    'GA': { name: 'Georgia', cities: ['Atlanta', 'Savannah', 'Augusta', 'Columbus'] },
    'NC': { name: 'North Carolina', cities: ['Charlotte', 'Raleigh', 'Durham', 'Greensboro'] },
    'WA': { name: 'Washington', cities: ['Seattle', 'Spokane', 'Tacoma', 'Bellevue'] },
    'IL': { name: 'Illinois', cities: ['Chicago', 'Aurora', 'Naperville', 'Rockford'] },
  };
  
  // Map city names to Redfin metro search terms
  // Some cities share a metro area (e.g., Lehigh Acres is in Cape Coral metro)
  const cityToMetroMap = {
    // Florida
    'Miami': 'Miami',
    'Tampa': 'Tampa',
    'Orlando': 'Orlando',
    'Jacksonville': 'Jacksonville',
    'Fort Lauderdale': 'Fort Lauderdale',
    'West Palm Beach': 'West Palm Beach',
    'Naples': 'Naples',
    'Sebring': 'Sebring',
    'Fort Myers': 'Cape Coral',  // Fort Myers is in Cape Coral metro
    'Lehigh Acres': 'Cape Coral', // Lehigh Acres is in Cape Coral metro
    // California
    'Los Angeles': 'Los Angeles',
    'San Francisco': 'San Francisco',
    'San Diego': 'San Diego',
    'San Jose': 'San Jose',
    'Sacramento': 'Sacramento',
    // Texas
    'Houston': 'Houston',
    'Dallas': 'Dallas',
    'Austin': 'Austin',
    'San Antonio': 'San Antonio',
    'Fort Worth': 'Fort Worth',
    // Other states use city name directly
  };
  
  // Get the Redfin metro search term for a city
  const getMetroSearchTerm = (city) => cityToMetroMap[city] || city;
  
  // Handle state change - reset city to first city in state
  const handleStateChange = (newState) => {
    const firstCity = statesWithCities[newState]?.cities[0] || '';
    setSelectedState(newState);
    setSelectedCity(firstCity);
    
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Directly trigger fetch with the new values with small debounce
    fetchTimeoutRef.current = setTimeout(() => {
      currentRequestRef.current += 1;
      const thisRequestId = currentRequestRef.current;
      fetchMarketData(firstCity, newState, thisRequestId);
    }, 50);
  };
  
  // Handle city change within same state
  const handleCityChange = (newCity) => {
    setSelectedCity(newCity);
    
    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Directly trigger fetch with small debounce
    fetchTimeoutRef.current = setTimeout(() => {
      currentRequestRef.current += 1;
      const thisRequestId = currentRequestRef.current;
      fetchMarketData(newCity, selectedState, thisRequestId);
    }, 50);
  };
  
  // AbortController ref for canceling previous requests
  const abortControllerRef = useRef(null);
  // Debounce ref to prevent double fetches
  const fetchTimeoutRef = useRef(null);
  
  // Initial fetch on mount
  useEffect(() => {
    // Small delay to ensure component is fully mounted
    fetchTimeoutRef.current = setTimeout(() => {
      currentRequestRef.current += 1;
      const thisRequestId = currentRequestRef.current;
      fetchMarketData(selectedCity, selectedState, thisRequestId);
    }, 100);
    
    // Cleanup function to abort on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []); // Only run on mount
  
  const fetchMarketData = async (cityLocation, stateCode, thisRequestId) => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setLoading(true);
    // Don't clear data - let new data replace old data to prevent flashing
    
    // Get the correct Redfin metro search term for this city
    const metroSearchTerm = getMetroSearchTerm(cityLocation);
    console.log(`Fetching data for: ${cityLocation} -> ${metroSearchTerm} (Request #${thisRequestId})`);
    
    try {
      // Fetch data sources with individual error handling
      const headers = { Authorization: `Bearer ${token}` };
      const signal = abortController.signal;
      
      // Run all fetches in PARALLEL using Promise.allSettled
      const [snapshotResult, mortgageResult, hpiResult, redfinNationalResult, metroResult] = await Promise.allSettled([
        // Snapshot with single retry
        (async () => {
          try {
            const res = await axios.get(`${API}/market-data/snapshot/${encodeURIComponent(cityLocation)}`, { headers, timeout: 15000, signal });
            return res.data;
          } catch (e) {
            // Retry once
            if (e.name !== 'CanceledError') {
              await new Promise(r => setTimeout(r, 500));
              const res = await axios.get(`${API}/market-data/snapshot/${encodeURIComponent(cityLocation)}`, { headers, timeout: 15000, signal });
              return res.data;
            }
            throw e;
          }
        })(),
        axios.get(`${API}/market-data/fred/MORTGAGE30US`, { headers, timeout: 15000, signal }).then(r => r.data),
        axios.get(`${API}/market-data/fhfa/${stateCode}`, { headers, timeout: 15000, signal }).then(r => r.data),
        axios.get(`${API}/market-data/redfin/national`, { headers, timeout: 20000, signal }).then(r => r.data),
        axios.get(`${API}/market-data/redfin/metros?metro=${encodeURIComponent(metroSearchTerm)}`, { headers, timeout: 20000, signal }).then(r => r.data)
      ]);
      
      // Extract successful results
      const snapshot = snapshotResult.status === 'fulfilled' ? snapshotResult.value : null;
      const mortgage = mortgageResult.status === 'fulfilled' ? mortgageResult.value : null;
      const hpi = hpiResult.status === 'fulfilled' ? hpiResult.value : null;
      const redfinNational = redfinNationalResult.status === 'fulfilled' ? redfinNationalResult.value : null;
      const metroData = metroResult.status === 'fulfilled' ? metroResult.value : null;
      
      // Log failures for debugging
      if (snapshotResult.status === 'rejected' && snapshotResult.reason?.name !== 'CanceledError') {
        console.warn('Snapshot fetch failed:', snapshotResult.reason?.message);
      }
      if (mortgageResult.status === 'rejected' && mortgageResult.reason?.name !== 'CanceledError') {
        console.warn('FRED fetch failed:', mortgageResult.reason?.message);
      }
      if (hpiResult.status === 'rejected' && hpiResult.reason?.name !== 'CanceledError') {
        console.warn('FHFA fetch failed:', hpiResult.reason?.message);
      }
      if (metroData) {
        console.log(`Got metro data for ${metroSearchTerm}:`, metroData?.results?.[0]?.metro);
      }
      
      // Only update state if this is still the current request (prevents race conditions)
      if (thisRequestId === currentRequestRef.current) {
        console.log(`Updating UI for request #${thisRequestId} (current: ${currentRequestRef.current})`);
        if (snapshot) setMarketSnapshot(snapshot);
        if (mortgage) setMortgageData(mortgage);
        if (hpi) setHpiData(hpi);
        if (redfinNational) setRedfinData(redfinNational);
        if (metroData) setRedfinMetro(metroData);
        
        // Show error only if ALL fetches failed
        if (!snapshot && !mortgage && !metroData) {
          toast.error('Failed to load market data');
        }
      } else {
        console.log(`Skipping stale request #${thisRequestId} (current: ${currentRequestRef.current})`);
      }
    } catch (error) {
      // Only show error if not aborted
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Error fetching market data:', error);
        toast.error('Failed to load market data');
      }
    } finally {
      // Always stop loading for this request
      if (thisRequestId === currentRequestRef.current) {
        setLoading(false);
      }
    }
  };

  // Ref to track if component is mounted
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  
  const generateMarketUpdate = async () => {
    if (!selectedCity) {
      toast.error('Please select a city first');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/market-data/generate-update`,
        {
          location: selectedCity,
          include_mortgage_rates: true,
          include_price_trends: true,
          include_inventory: true,
          tone: 'Professional'
        },
        { headers: authHeaders, timeout: 60000 }  // Increase timeout for AI generation
      );
      
      setGeneratedContent(response.data);
      setActiveTab('content');
      toast.success('Market update generated!');
    } catch (error) {
      console.error('Error generating update:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Please try again.');
      } else if (error.response?.status === 500) {
        toast.error('AI service temporarily unavailable. Please try again.');
      } else {
        toast.error('Failed to generate market update');
      }
    } finally {
      setGenerating(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };
  
  const formatPercent = (value) => {
    return `${value > 0 ? '+' : ''}${value}%`;
  };
  
  // Generate HTML email template for copying
  const generateEmailHTML = () => {
    if (!generatedContent) return '';
    
    const medianPrice = redfinMetro?.results?.[0]?.median_sale_price || marketSnapshot?.metrics?.median_home_value || 0;
    const priceYoY = redfinMetro?.results?.[0]?.median_sale_price_yoy || marketSnapshot?.metrics?.yoy_price_change || 0;
    const mortgageRate = mortgageData?.data?.[0]?.value || '6.1';
    const daysOnMarket = redfinMetro?.results?.[0]?.median_dom || marketSnapshot?.metrics?.days_on_market || 30;
    const inventoryMonths = redfinMetro?.results?.[0]?.months_of_supply || marketSnapshot?.metrics?.inventory_months || 3;
    const agent = generatedContent.agent_profile || {};
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <!-- Header -->
    <tr>
      <td style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); padding: 30px 20px; text-align: center;">
        <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">${selectedCity} Market Update</h1>
        <p style="color: rgba(255,255,255,0.9); margin: 8px 0 0 0; font-size: 14px;">${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
      </td>
    </tr>
    
    <!-- Stats Grid -->
    <tr>
      <td style="padding: 20px;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <!-- Median Price -->
            <td width="50%" style="padding: 8px;">
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; text-align: center;">
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #64748b; text-transform: uppercase;">Median Price</p>
                <p style="margin: 0; font-size: 22px; font-weight: bold; color: #1e293b;">${formatCurrency(medianPrice)}</p>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: ${priceYoY >= 0 ? '#16a34a' : '#dc2626'}; font-weight: 600;">${formatPercent(priceYoY)} YoY</p>
              </div>
            </td>
            <!-- Mortgage Rate -->
            <td width="50%" style="padding: 8px;">
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; text-align: center;">
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #64748b; text-transform: uppercase;">Mortgage Rate</p>
                <p style="margin: 0; font-size: 22px; font-weight: bold; color: #1e293b;">${mortgageRate}%</p>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">30-Year Fixed</p>
              </div>
            </td>
          </tr>
          <tr>
            <!-- Days on Market -->
            <td width="50%" style="padding: 8px;">
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; text-align: center;">
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #64748b; text-transform: uppercase;">Days on Market</p>
                <p style="margin: 0; font-size: 22px; font-weight: bold; color: #1e293b;">${daysOnMarket}</p>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">Median DOM</p>
              </div>
            </td>
            <!-- Inventory -->
            <td width="50%" style="padding: 8px;">
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; text-align: center;">
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #64748b; text-transform: uppercase;">Inventory</p>
                <p style="margin: 0; font-size: 22px; font-weight: bold; color: #1e293b;">${inventoryMonths} mo</p>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">Supply</p>
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    
    <!-- Content -->
    <tr>
      <td style="padding: 0 20px 20px 20px;">
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px;">
          <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #374151;">
            ${generatedContent.content.email_body?.replace(/\n\n/g, '</p><p style="margin: 16px 0 0 0; font-size: 14px; line-height: 1.6; color: #374151;">').replace(/\n/g, '<br>') || ''}
          </p>
        </div>
      </td>
    </tr>
    
    <!-- Agent Footer -->
    <tr>
      <td style="padding: 20px; border-top: 1px solid #e2e8f0; background-color: #f8fafc;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td width="60" style="vertical-align: top;">
              ${agent.headshot_photo 
                ? `<img src="${agent.headshot_photo}" alt="${agent.name}" style="width: 50px; height: 50px; border-radius: 50%; object-fit: cover;">`
                : `<div style="width: 50px; height: 50px; border-radius: 50%; background-color: #3b82f6; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 18px;">${(agent.name || 'A').charAt(0)}</div>`
              }
            </td>
            <td style="vertical-align: top; padding-left: 12px;">
              <p style="margin: 0; font-size: 16px; font-weight: 600; color: #1e293b;">${agent.name || 'Your Real Estate Agent'}</p>
              <p style="margin: 4px 0 0 0; font-size: 13px; color: #64748b;">${agent.brokerage_name || 'Real Estate Professional'}</p>
              <p style="margin: 8px 0 0 0; font-size: 13px; color: #3b82f6;">
                ${agent.phone ? `${agent.phone}` : ''}${agent.phone && agent.email ? ' • ' : ''}${agent.email || ''}
              </p>
            </td>
            ${agent.brokerage_logo ? `
            <td width="100" style="vertical-align: middle; text-align: right; padding-left: 10px;">
              <div style="width: 80px; height: 50px; overflow: hidden; display: inline-block;">
                <img src="${agent.brokerage_logo}" alt="Brokerage" width="80" style="max-width: 80px; max-height: 50px; width: 80px; height: auto; display: block;">
              </div>
            </td>
            ` : ''}
          </tr>
        </table>
      </td>
    </tr>
    
    <!-- Footer -->
    <tr>
      <td style="padding: 16px 20px; text-align: center; background-color: #1e293b;">
        <p style="margin: 0; font-size: 12px; color: #94a3b8;">
          Generated with ListingPro AI • ${new Date().toLocaleDateString()}
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
  };

  const copyEmailHTML = async () => {
    const html = generateEmailHTML();
    try {
      // Try to copy as HTML first (for rich text paste)
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = new Blob([html], { type: 'text/html' });
        const clipboardItem = new ClipboardItem({ 'text/html': blob });
        await navigator.clipboard.write([clipboardItem]);
        toast.success('HTML email copied! Paste into your email client.');
      } else {
        // Fallback to text
        await navigator.clipboard.writeText(html);
        toast.success('HTML code copied! Paste into your email editor.');
      }
    } catch (err) {
      // Final fallback - create a temporary textarea
      try {
        const textarea = document.createElement('textarea');
        textarea.value = html;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        toast.success('HTML code copied! Paste into your email editor.');
      } catch (fallbackErr) {
        toast.error('Unable to copy. Please select and copy the HTML manually.');
      }
    }
  };
  
  const getMarketTempColor = (temp) => {
    switch (temp) {
      case 'hot': return 'bg-red-500';
      case 'warm': return 'bg-orange-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="shrink-0"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="text-primary" />
                  Market Data
                </h1>
                <p className="text-muted-foreground mt-1">
                  Real estate market insights and trends
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* State Selector */}
              <div className="flex items-center gap-2">
                <Label htmlFor="state" className="text-sm whitespace-nowrap">State:</Label>
                <Select value={selectedState} onValueChange={handleStateChange}>
                  <SelectTrigger className="w-[150px]" id="state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statesWithCities).map(([code, { name }]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* City Selector */}
              <div className="flex items-center gap-2">
                <Label htmlFor="city" className="text-sm whitespace-nowrap">City:</Label>
                <Select value={selectedCity} onValueChange={handleCityChange}>
                  <SelectTrigger className="w-[160px]" id="city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {statesWithCities[selectedState]?.cities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={() => { currentRequestRef.current += 1; fetchMarketData(selectedCity, selectedState, currentRequestRef.current); }} variant="outline" size="icon" disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
              
              <Button onClick={generateMarketUpdate} disabled={generating || loading} className="gap-2">
                {generating ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Share2 size={16} />
                    Create Market Update
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 size={16} />
              Overview
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp size={16} />
              Trends
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <Mail size={16} />
              Generated Content
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw size={32} className="animate-spin text-primary" />
              </div>
            ) : marketSnapshot ? (
              <div className="space-y-6">
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Home className="text-primary" size={20} />
                        </div>
                        {redfinMetro?.results?.[0]?.median_sale_price ? (
                          <Badge className="bg-green-500">LIVE</Badge>
                        ) : (
                          <Badge className={marketSnapshot.metrics.yoy_price_change > 0 ? 'bg-green-500' : 'bg-red-500'}>
                            {formatPercent(marketSnapshot.metrics.yoy_price_change)} YoY
                          </Badge>
                        )}
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-muted-foreground">Median Sale Price</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(redfinMetro?.results?.[0]?.median_sale_price || marketSnapshot.metrics.median_home_value)}
                        </p>
                        {redfinMetro?.results?.[0]?.median_sale_price_yoy && (
                          <p className={`text-xs ${redfinMetro.results[0].median_sale_price_yoy > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercent(redfinMetro.results[0].median_sale_price_yoy)} YoY
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Source: {redfinMetro?.results?.[0] ? 'Redfin' : 'Estimate'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <Building2 className="text-blue-500" size={20} />
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-muted-foreground">Median Rent</p>
                        <p className="text-2xl font-bold">{formatCurrency(marketSnapshot.metrics.median_rent)}/mo</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="p-2 bg-orange-500/10 rounded-lg">
                          <Percent className="text-orange-500" size={20} />
                        </div>
                        {mortgageData?.api_status === 'live' && (
                          <Badge className="bg-green-500">LIVE</Badge>
                        )}
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-muted-foreground">30-Year Mortgage Rate</p>
                        <p className="text-2xl font-bold">
                          {mortgageData?.data?.[0]?.value || marketSnapshot.metrics.mortgage_rate_30yr}%
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Source: {mortgageData?.source || 'FRED'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="p-2 bg-purple-500/10 rounded-lg">
                          <Clock className="text-purple-500" size={20} />
                        </div>
                        {redfinMetro?.results?.[0]?.median_dom && (
                          <Badge className="bg-green-500">LIVE</Badge>
                        )}
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-muted-foreground">Days on Market</p>
                        <p className="text-2xl font-bold">
                          {redfinMetro?.results?.[0]?.median_dom || marketSnapshot.metrics.days_on_market} days
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Source: {redfinMetro?.results?.[0] ? 'Redfin' : 'Estimate'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Redfin Local Data Section */}
                {(redfinMetro?.results?.length > 0 || redfinData?.current_metrics) && (
                  <Card className="border-2 border-primary/20">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <MapPin className="text-primary" size={20} />
                            Live Market Data - {selectedCity}
                          </CardTitle>
                          <CardDescription>Real-time data from Redfin</CardDescription>
                        </div>
                        <Badge className="bg-green-500">LIVE DATA</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Metro-specific data if available */}
                        {redfinMetro?.results?.[0] && (
                          <>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Metro Median Price</p>
                              <p className="text-xl font-bold text-primary">
                                {formatCurrency(redfinMetro.results[0].median_sale_price)}
                              </p>
                              {redfinMetro.results[0].median_sale_price_yoy && (
                                <p className={`text-xs ${redfinMetro.results[0].median_sale_price_yoy > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(redfinMetro.results[0].median_sale_price_yoy)} YoY
                                </p>
                              )}
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Days on Market</p>
                              <p className="text-xl font-bold">{redfinMetro.results[0].median_dom} days</p>
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Homes Sold</p>
                              <p className="text-xl font-bold">{redfinMetro.results[0].homes_sold?.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Supply</p>
                              <p className="text-xl font-bold">{redfinMetro.results[0].months_of_supply} months</p>
                            </div>
                          </>
                        )}
                        
                        {/* National data as fallback */}
                        {!redfinMetro?.results?.[0] && redfinData?.current_metrics && (
                          <>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">National Median</p>
                              <p className="text-xl font-bold text-primary">
                                {formatCurrency(redfinData.current_metrics.median_sale_price)}
                              </p>
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Days on Market</p>
                              <p className="text-xl font-bold">{redfinData.current_metrics.median_days_on_market} days</p>
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Active Listings</p>
                              <p className="text-xl font-bold">{redfinData.current_metrics.active_inventory?.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-xs text-muted-foreground mb-1">Sale-to-List</p>
                              <p className="text-xl font-bold">{redfinData.current_metrics.sale_to_list_ratio}%</p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {redfinMetro?.results?.[0] && (
                        <p className="text-xs text-muted-foreground mt-3">
                          Data for: {redfinMetro.results[0].metro} • Updated: {redfinMetro.results[0].period_end?.slice(0, 10)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Additional Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Market Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-muted-foreground">5-Year Price Change</span>
                          <span className="font-semibold text-green-600">
                            {formatPercent(marketSnapshot.metrics.five_year_price_change)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-muted-foreground">Price per Sq Ft</span>
                          <span className="font-semibold">${marketSnapshot.metrics.price_per_sqft}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-muted-foreground">Inventory Supply</span>
                          <span className="font-semibold">{marketSnapshot.metrics.inventory_months} months</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-muted-foreground">Price Trend</span>
                          <span className={`font-semibold flex items-center gap-1 ${
                            marketSnapshot.trends.price_trend === 'up' ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {marketSnapshot.trends.price_trend === 'up' ? (
                              <><TrendingUp size={16} /> Rising</>
                            ) : (
                              <><TrendingDown size={16} /> Stable</>
                            )}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Market Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="p-3 bg-secondary/50 rounded-lg">
                          <p className="text-sm">
                            <strong>For Buyers:</strong> With {marketSnapshot.metrics.inventory_months} months of inventory, 
                            this is a {marketSnapshot.trends.market_temp} market. Consider acting quickly on well-priced properties.
                          </p>
                        </div>
                        <div className="p-3 bg-secondary/50 rounded-lg">
                          <p className="text-sm">
                            <strong>For Sellers:</strong> Home values have increased {formatPercent(marketSnapshot.metrics.yoy_price_change)} year-over-year. 
                            Properties are averaging {marketSnapshot.metrics.days_on_market} days on market.
                          </p>
                        </div>
                        <div className="p-3 bg-secondary/50 rounded-lg">
                          <p className="text-sm">
                            <strong>For Investors:</strong> The median rent of {formatCurrency(marketSnapshot.metrics.median_rent)}/month 
                            represents a strong rental market opportunity.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="text-center py-20 text-muted-foreground">
                Select a location to view market data
              </div>
            )}
          </TabsContent>
          
          {/* Trends Tab */}
          <TabsContent value="trends">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Mortgage Rate Trends */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Percent className="text-primary" size={20} />
                        30-Year Mortgage Rate Trend
                      </CardTitle>
                      <CardDescription>Historical mortgage rates from FRED</CardDescription>
                    </div>
                    {mortgageData?.api_status === 'live' && (
                      <Badge className="bg-green-500">LIVE</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {mortgageData?.data ? (
                    <SimpleLineChart data={[...mortgageData.data].reverse().slice(-24)} height={200} />
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      Loading...
                    </div>
                  )}
                  {mortgageData?.data?.[0] && (
                    <div className="mt-4 flex justify-between text-sm">
                      <span className="text-muted-foreground">Current Rate</span>
                      <span className="font-semibold text-lg">{mortgageData.data[0]?.value}%</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* House Price Index */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Home className="text-primary" size={20} />
                    House Price Index - {selectedCity}
                  </CardTitle>
                  <CardDescription>FHFA House Price Index trends</CardDescription>
                </CardHeader>
                <CardContent>
                  {hpiData?.data ? (
                    <SimpleLineChart data={hpiData.data} height={200} />
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      Loading...
                    </div>
                  )}
                  {hpiData && (
                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">YoY Change</span>
                        <p className="font-semibold text-green-600">{formatPercent(hpiData.yoy_change)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">5-Year Change</span>
                        <p className="font-semibold text-green-600">{formatPercent(hpiData.five_year_change)}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Generated Content Tab */}
          <TabsContent value="content">
            {generatedContent ? (
              <div className="space-y-6">
                {/* Back to Dashboard Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={() => navigate('/dashboard')} 
                    variant="outline"
                    className="gap-2"
                  >
                    <ArrowLeft size={16} />
                    Back to Dashboard
                  </Button>
                </div>
                
                {/* Agent Branding Preview */}
                {generatedContent.agent_profile && (
                  <Card className="bg-gradient-to-r from-primary/5 to-primary/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {generatedContent.agent_profile.headshot_photo ? (
                          <img 
                            src={generatedContent.agent_profile.headshot_photo} 
                            alt="Agent" 
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                            <Users size={24} className="text-primary" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{generatedContent.agent_profile.name}</p>
                          <p className="text-sm text-muted-foreground">{generatedContent.agent_profile.brokerage_name || 'Real Estate Professional'}</p>
                          <div className="flex gap-3 mt-1 text-sm text-muted-foreground">
                            {generatedContent.agent_profile.phone && <span>{generatedContent.agent_profile.phone}</span>}
                            {generatedContent.agent_profile.email && <span>{generatedContent.agent_profile.email}</span>}
                          </div>
                        </div>
                        {generatedContent.agent_profile.brokerage_logo && (
                          <img 
                            src={generatedContent.agent_profile.brokerage_logo} 
                            alt="Brokerage" 
                            className="h-12 object-contain"
                          />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        💡 Update your agent profile in Account Settings to customize your branding on market updates.
                      </p>
                    </CardContent>
                  </Card>
                )}
                
                {/* Email Content - Visual Preview */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Mail size={20} />
                        Email Marketing
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={copyEmailHTML}
                          className="gap-2"
                          data-testid="copy-html-email-btn"
                        >
                          <Copy size={14} />
                          Copy HTML Email
                        </Button>
                        <CopyButton 
                          text={`Subject: ${generatedContent.content.email_subject}\n\n${generatedContent.content.email_body}`} 
                          label="Text Only"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Click &ldquo;Copy HTML Email&rdquo; to copy the styled email with stats cards, then paste directly into Gmail, Outlook, or your email marketing tool.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">SUBJECT LINE</Label>
                      <p className="font-medium mt-1">{generatedContent.content.email_subject}</p>
                    </div>
                    
                    {/* Visual Email Preview */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 border-b">
                        <p className="text-xs text-muted-foreground mb-1">PREVIEW</p>
                        <p className="font-semibold text-lg">{selectedCity} Market Update</p>
                      </div>
                      
                      {/* Stats Cards Grid */}
                      <div className="p-4 bg-secondary/20">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm border">
                            <Home className="mx-auto mb-1 text-primary" size={20} />
                            <p className="text-xs text-muted-foreground">Median Price</p>
                            <p className="font-bold text-lg">{formatCurrency(redfinMetro?.results?.[0]?.median_sale_price || marketSnapshot?.metrics?.median_home_value || 0)}</p>
                            {(redfinMetro?.results?.[0]?.median_sale_price_yoy || marketSnapshot?.metrics?.yoy_price_change) && (
                              <p className={`text-xs ${(redfinMetro?.results?.[0]?.median_sale_price_yoy || marketSnapshot?.metrics?.yoy_price_change) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(redfinMetro?.results?.[0]?.median_sale_price_yoy || marketSnapshot?.metrics?.yoy_price_change)} YoY
                              </p>
                            )}
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm border">
                            <Percent className="mx-auto mb-1 text-orange-500" size={20} />
                            <p className="text-xs text-muted-foreground">Mortgage Rate</p>
                            <p className="font-bold text-lg">{mortgageData?.data?.[0]?.value || '6.1'}%</p>
                            <p className="text-xs text-muted-foreground">30-Year Fixed</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm border">
                            <Clock className="mx-auto mb-1 text-purple-500" size={20} />
                            <p className="text-xs text-muted-foreground">Days on Market</p>
                            <p className="font-bold text-lg">{redfinMetro?.results?.[0]?.median_dom || marketSnapshot?.metrics?.days_on_market || 30}</p>
                            <p className="text-xs text-muted-foreground">Median DOM</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center shadow-sm border">
                            <Building2 className="mx-auto mb-1 text-blue-500" size={20} />
                            <p className="text-xs text-muted-foreground">Inventory</p>
                            <p className="font-bold text-lg">{redfinMetro?.results?.[0]?.months_of_supply || marketSnapshot?.metrics?.inventory_months || 3} mo</p>
                            <p className="text-xs text-muted-foreground">Supply</p>
                          </div>
                        </div>
                        
                        {/* Key Insights */}
                        <div className="bg-white rounded-lg p-4 shadow-sm border">
                          <p className="text-sm leading-relaxed text-muted-foreground">
                            {generatedContent.content.email_body?.split('\n\n')[0] || 'Market insights will appear here...'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Agent Footer */}
                      {generatedContent.agent_profile && (
                        <div className="p-4 bg-white border-t flex items-center gap-3">
                          {generatedContent.agent_profile.headshot_photo ? (
                            <img src={generatedContent.agent_profile.headshot_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users size={16} className="text-primary" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{generatedContent.agent_profile.name}</p>
                            <p className="text-xs text-muted-foreground">{generatedContent.agent_profile.phone} • {generatedContent.agent_profile.email}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Full Text Version */}
                    <details className="mt-4">
                      <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                        View full text version
                      </summary>
                      <div className="mt-2 p-4 bg-secondary/30 rounded-lg whitespace-pre-wrap text-sm">
                        {generatedContent.content.email_body}
                      </div>
                    </details>
                  </CardContent>
                </Card>
                
                {/* Social Media Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Instagram / Twitter</CardTitle>
                        <CopyButton text={generatedContent.content.social_post_short} label="Short post" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-secondary/30 rounded-lg text-sm">
                        {generatedContent.content.social_post_short}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {generatedContent.content.social_post_short?.length || 0} / 280 characters
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Facebook / LinkedIn</CardTitle>
                        <CopyButton text={generatedContent.content.social_post_long} label="Long post" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-secondary/30 rounded-lg text-sm whitespace-pre-wrap">
                        {generatedContent.content.social_post_long}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Key Takeaways */}
                {generatedContent.content.key_takeaways && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Key Takeaways</CardTitle>
                        <CopyButton 
                          text={generatedContent.content.key_takeaways.join('\n• ')} 
                          label="Takeaways"
                        />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {generatedContent.content.key_takeaways.map((point, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight size={16} className="text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-20">
                <BarChart3 size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No market update generated yet</p>
                <Button onClick={generateMarketUpdate} disabled={generating}>
                  {generating ? 'Generating...' : 'Create Market Update'}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MarketData;
